import React, { useCallback, useMemo } from 'react';

import { message } from 'antd';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { Info } from '../../../icons/Info';
import { updateAccount } from '../../../stores/accounts';
import { useUpdateAmazonProductProviderMutation } from '../../../stores/productProvidersSlice';
import Modal from '../Modal';
import {
  BoldMessage,
  ButtonContainer,
  InfoMessage,
  InfoSection,
  ModalContent,
  StyledButton,
  Wrapper,
} from './SmidModalStyles';

export interface SmidDisconnectedModalMessages {
  title: string;
  explanation: string;
  howToFixTitle: string;
  connectSpApiButton: string;
  dontHaveSpApiButton: string;
  close: string;
  alreadyMarkedAsAVendor: string;
}

interface ProductProvider {
  id: string;
  accountType?: string;
  marketplaceId?: string;
}

interface Account {
  id: string;
  productProviders: ProductProvider[];
}

interface SmidDisconnectedModalProps {
  account: Account;
  onClose: () => void;
}

const SmidDisconnectedModal: React.FC<SmidDisconnectedModalProps> = ({ account, onClose }) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const smidMessages = messages.smidModal as unknown as SmidDisconnectedModalMessages;
  const [updateAmazonProductProvider, { isLoading }] = useUpdateAmazonProductProviderMutation();

  const productProvider = useMemo(() => account?.productProviders[0] || null, [account.productProviders]);
  const markUserAsVendor = useCallback(async () => {
    if (!productProvider) {
      message.error('Product provider not found.');

      return;
    }

    try {
      const result = await updateAmazonProductProvider({
        productProviderId: productProvider.id,
        accountId: account.id,
        data: { accountType: 'VENDOR' },
      });

      if (result.error) {
        throw new Error('Failed to update account type');
      }

      message.success(
        'Account type updated successfully. You can connect to SP-API later from your Amazon account settings.',
      );
      const { provider } = result.data;

      if (provider) {
        const updatedAccount = {
          ...account,
          productProviders: account.productProviders.map((pp) => (pp.id === productProvider.id ? provider : pp)),
        };

        // update store with updated account
        dispatch(updateAccount(updatedAccount));
      }

      onClose();
    } catch {
      message.error('Failed to update account type. Please try again.');
    }
  }, [updateAmazonProductProvider, account, onClose]);

  if (!account || !productProvider) {
    onClose();

    return null;
  }

  return (
    <Modal
      title={smidMessages.title}
      handleCancel={onClose}
      handleOk={onClose}
      displayButtons={false}
      className="modal-mask--dark"
      footer={<></>}
    >
      <ModalContent>
        {productProvider.accountType === 'VENDOR' ? (
          <>
            <InfoSection>
              <Info width="18px" height="18px" />
              <Wrapper>{smidMessages.alreadyMarkedAsAVendor}</Wrapper>
            </InfoSection>
            <ButtonContainer>
              <StyledButton as={Link} to={`/${account.id}/amazon/account`}>
                {smidMessages.connectSpApiButton}
              </StyledButton>

              <StyledButton onClick={() => onClose()}>{smidMessages.close}</StyledButton>
            </ButtonContainer>
          </>
        ) : productProvider.accountType === 'SELLER' ? (
          <>
            <InfoSection>
              <Info width="18px" height="18px" />
              <Wrapper>
                <InfoMessage>{smidMessages.explanation}</InfoMessage>
                <BoldMessage>{smidMessages.howToFixTitle}</BoldMessage>
              </Wrapper>
            </InfoSection>

            <ButtonContainer>
              <StyledButton as={Link} to={`/${account.id}/amazon/account`}>
                {smidMessages.connectSpApiButton}
              </StyledButton>
            </ButtonContainer>
          </>
        ) : (
          <>
            <InfoSection>
              <Info width="18px" height="18px" />
              <Wrapper>
                <InfoMessage>{smidMessages.explanation}</InfoMessage>
                <BoldMessage>{smidMessages.howToFixTitle}</BoldMessage>
              </Wrapper>
            </InfoSection>

            <ButtonContainer>
              <StyledButton as={Link} to={`/${account.id}/amazon/account`}>
                {smidMessages.connectSpApiButton}
              </StyledButton>
              <StyledButton onClick={() => markUserAsVendor()} disabled={isLoading}>
                {isLoading ? <span>Submitting...</span> : smidMessages.dontHaveSpApiButton}
              </StyledButton>
            </ButtonContainer>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default SmidDisconnectedModal;
